<script setup>
/**
 * @fileoverview SharePortalConsole — opens or focuses a share console session.
 *
 * Route: `/share/:shareId/vms/:vmId/console`. The global `consoleSessions` store
 * and `ConsoleSessionsHost` (mounted in `SharePortalLayout`) render the viewer.
 * Navigating away keeps WebSockets alive until idle TTL or close.
 */
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useShareSession } from '@/composables/useShareSession'
import { useConsoleSessionsStore } from '@/stores/consoleSessions'
import { getShareVMs } from '@/api/sharePortal'

const route = useRoute()
const { checkSession, labName, loadShareInfo } = useShareSession()
const sessionsStore = useConsoleSessionsStore()

const shareId = computed(() => String(route.params.shareId || ''))
const vmId = computed(() => String(route.params.vmId || ''))

const vmName = ref('')
const loadError = ref(null)

onMounted(async () => {
  if (!checkSession()) {
    return
  }

  if (!shareId.value || !vmId.value) {
    loadError.value = 'Invalid share or VM link'
    return
  }

  try {
    await loadShareInfo(shareId.value)
  } catch (err) {
    console.warn('[SharePortalConsole] loadShareInfo:', err)
  }

  try {
    const list = await getShareVMs(shareId.value)
    const vms = Array.isArray(list) ? list : list?.data || []
    const vm = vms.find(v => v.id === vmId.value)
    vmName.value = vm?.name || vmId.value
  } catch (err) {
    console.warn('[SharePortalConsole] Could not load VM list:', err)
    vmName.value = vmId.value
  }

  const result = sessionsStore.openShareSession({
    shareId: shareId.value,
    vmId: vmId.value,
    vmName: vmName.value,
    labName: labName.value || ''
  })
  if (!result.ok) {
    loadError.value =
      result.reason === 'session limit reached'
        ? 'Too many open console sessions. Close one from the dock, then try again.'
        : 'Could not open console session.'
  }
})
</script>

<template>
  <div
    class="flex min-h-screen w-full flex-col items-center justify-center bg-slate-900 px-6 py-12 text-center text-white"
  >
    <p v-if="loadError" class="max-w-md text-sm text-red-200">{{ loadError }}</p>
    <template v-else>
      <p class="text-sm text-slate-300">
        Console is active. Use <strong class="text-white">Switch VM</strong> in the toolbar to open
        another VM, or the dock when minimized.
      </p>
    </template>
  </div>
</template>
